import React, { type TouchEvent, useState } from "react";

import {
  DeleteConfirmModal,
  EnlargedImageModal,
  ImageCropModal,
  ImageGalleryHeader,
  ImageThumbnail,
  LoadingPlaceholder,
} from "@/components/AvatarGeneratorModal/components/ImageGallery";
import { useScrollIntoView } from "@/components/AvatarGeneratorModal/hooks/useScrollIntoView";
import { type AvatarGeneratedImage, type IImageGalleryProps } from "@/components/AvatarGeneratorModal/types";
import { cn } from "@/utils";

const DEFAULT_IMAGE_EXTENSION = "webp";

export const ImageGallery = ({
  generatedImages,
  currentImageIndex,
  isAvatarGenerating,
  isDragging,
  containerProps,
  prompt,
  modelStyle,
  onNavigatePrev,
  onNavigateNext,
  onImageSelect,
  onImageEdit,
  onImageCrop,
  onImageDelete,
}: IImageGalleryProps) => {
  const { className: containerClassName, ref: scrollContainerRef, ...restContainerProps } = containerProps;
  const [enlargedImageIndex, setEnlargedImageIndex] = useState<number | null>(null);
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);
  const [deleteImageIndex, setDeleteImageIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const imageRefs = useScrollIntoView<HTMLDivElement>(currentImageIndex);
  const minSwipeDistance = 50;

  const handleDeleteConfirm = () => {
    if (deleteImageIndex !== null && onImageDelete) {
      onImageDelete(deleteImageIndex);
      setDeleteImageIndex(null);
    }
  };

  const handleEnlargedPrev = () => {
    if (enlargedImageIndex !== null && enlargedImageIndex > 0) {
      const newIndex = enlargedImageIndex - 1;
      setEnlargedImageIndex(newIndex);
      onImageSelect(newIndex);
    }
  };

  const handleEnlargedNext = () => {
    if (enlargedImageIndex !== null && enlargedImageIndex < generatedImages.length - 1) {
      const newIndex = enlargedImageIndex + 1;
      setEnlargedImageIndex(newIndex);
      onImageSelect(newIndex);
    }
  };

  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleEnlargedNext();
    } else if (isRightSwipe) {
      handleEnlargedPrev();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef?.current) return;
    setIsMouseDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsMouseDown(false);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isMouseDown || !scrollContainerRef?.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const generateFilename = (index: number, extension: string): string => {
    const promptPart = prompt
      .slice(0, 60)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");

    const modelName = modelStyle.toLowerCase().replace(/_/g, "-");
    return `${promptPart}-${modelName}-${index + 1}.${extension}`;
  };

  const downloadImage = (image: AvatarGeneratedImage, index: number) => {
    const extension = image.type?.split("/")[1] || DEFAULT_IMAGE_EXTENSION;
    const filename = generateFilename(index, extension);
    const link = document.createElement("a");
    link.href = `data:${image.type};base64,${image.base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!generatedImages.length && !isAvatarGenerating) {
    return null;
  }

  const enlargedImage = enlargedImageIndex !== null ? generatedImages[enlargedImageIndex] : null;

  return (
    <>
      <div className="flex flex-col gap-sm">
        <ImageGalleryHeader
          totalImages={generatedImages.length}
          currentImageIndex={currentImageIndex}
          onNavigatePrev={onNavigatePrev}
          onNavigateNext={onNavigateNext}
        />

        <div
          {...restContainerProps}
          ref={scrollContainerRef}
          className={cn("flex overflow-x-auto pb-4 whitespace-nowrap scrollbar-hide cursor-grab", containerClassName)}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onDragStart={handleDragStart}
          style={{ userSelect: isMouseDown ? "none" : "auto" }}
        >
          <div className="flex gap-sm items-start">
            {isAvatarGenerating && <LoadingPlaceholder />}
            {generatedImages.map((image: AvatarGeneratedImage, index: number) => (
              <ImageThumbnail
                key={image.id}
                image={image}
                index={index}
                isSelected={currentImageIndex === index}
                isAvatarGenerating={isAvatarGenerating}
                isDragging={isDragging}
                imageRef={(el: HTMLDivElement | null) => (imageRefs.current[index] = el)}
                onImageSelect={onImageSelect}
                setEnlargedImageIndex={setEnlargedImageIndex}
                onEdit={onImageEdit ? () => onImageEdit(index) : undefined}
                onDownload={() => downloadImage(image, index)}
                onCrop={onImageCrop ? () => setCropImageIndex(index) : undefined}
                onDelete={onImageDelete ? () => setDeleteImageIndex(index) : undefined}
              />
            ))}
          </div>
        </div>
      </div>

      <EnlargedImageModal
        image={enlargedImage}
        imageIndex={enlargedImageIndex}
        totalImages={generatedImages.length}
        onClose={() => setEnlargedImageIndex(null)}
        onPrev={handleEnlargedPrev}
        onNext={handleEnlargedNext}
        onDownload={() => enlargedImageIndex !== null && enlargedImage && downloadImage(enlargedImage, enlargedImageIndex)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      />

      {cropImageIndex !== null && (
        <ImageCropModal
          open={true}
          onOpenChange={(open: boolean) => {
            if (!open) setCropImageIndex(null);
          }}
          imageSrc={`data:${generatedImages[cropImageIndex]?.type};base64,${generatedImages[cropImageIndex]?.base64}`}
          imageType={generatedImages[cropImageIndex]?.type || "image/webp"}
          onCropComplete={(croppedBase64: string) => {
            if (onImageCrop) {
              onImageCrop(cropImageIndex, croppedBase64);
            }
            setCropImageIndex(null);
          }}
        />
      )}

      <DeleteConfirmModal isOpen={deleteImageIndex !== null} onConfirm={handleDeleteConfirm} onCancel={() => setDeleteImageIndex(null)} />
    </>
  );
};
